import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import { fetchCreateTestLinkToken, fetchWithAuth } from "../../../../../api-client/APIClient";
import { LinkedAccount, LinkedAccountStatus } from "../../../../../models/Entities";
import CustomAddTestLinkedAccountButton from "./CustomAddTestLinkedAccountButton";
import useAppContext from "../../../../context/useAppContext";
import { isBillingPlanFreeLaunch } from "../../../settings/billing/BillingUtils";
import useProductRestrictions from "../../../../shared/hooks/useProductRestrictions";
import { Text } from "@merge-api/merge-javascript-shared";
import TileStep from "../shared/TileStep";

export interface TileWithImageProps {
  img: React.ReactElement;
  title: string;
  subtitle?: JSX.Element;
  stepNumber: number;
  linkedAccounts: LinkedAccount[] | null;
  numberOfAccounts?: number;
  setNumberOfAccounts: Dispatch<SetStateAction<number | undefined>>;
  setLinkedAccounts: Dispatch<SetStateAction<LinkedAccount[]>>;
  setLinkedAccount: Dispatch<SetStateAction<LinkedAccount | null>>;
}

const CustomTileStep = ({
  img,
  title,
  subtitle,
  stepNumber,
  linkedAccounts,
  numberOfAccounts,
  setNumberOfAccounts,
  setLinkedAccount,
  setLinkedAccounts,
}: TileWithImageProps) => {
  const [linkToken, setLinkToken] = useState<string | undefined>();
  const { user } = useAppContext();
  const { orgBillingPlan } = useProductRestrictions();
  const [orgHasReachedMaxLinkedAccounts, setOrgHasReachedMaxLinkedAccounts] = useState(false);
  const [renderOrgPlan, setRenderOrgPlan] = useState(false);

  /*
   *  First check to see if our API call has returned our list of Linked Accounts (numberOfAccounts is set to undefined until then)
   *  If they are not undefined, sets state for if the Org is launch and has reached max link accounts
   *  Sets the state of orgHasReachedMaxLinkedAccounts, and sets renderOrgPlan to true
   *  If orgHasReachedMaxLinkedAccounts isn't true, and renderOrgPlan is true, calls fetchCreateTestLinkToken
   *
   */

  useEffect(() => {
    if (numberOfAccounts !== undefined) {
      setOrgHasReachedMaxLinkedAccounts(
        isBillingPlanFreeLaunch(user.organization.organization_billing_plan?.billing_plan) &&
          numberOfAccounts >= (orgBillingPlan?.product_restrictions?.test_accounts ?? 3),
      );
      setRenderOrgPlan(true);

      if (!orgHasReachedMaxLinkedAccounts && renderOrgPlan) {
        fetchCreateTestLinkTokenConditionally();
      }
    }
  }, [numberOfAccounts, renderOrgPlan]);

  /*
   *  Fetches a create-test-link-token to create a linked account
   */

  const fetchCreateTestLinkTokenConditionally = () => {
    if (!orgHasReachedMaxLinkedAccounts) {
      fetchCreateTestLinkToken(setLinkToken);
    }
  };

  const fetchNewLinkedAccounts = () => {
    fetchWithAuth({
      path: `/integrations/linked-accounts?status=${LinkedAccountStatus.COMPLETE}&is_test_account=true&page_size=100`,
      method: "GET",
      onResponse: (data) => {
        setLinkedAccounts(data.results);
        if (linkedAccounts) {
          const firstLinkedAccount = data.results[0];
          setLinkedAccount(firstLinkedAccount);
          setNumberOfAccounts(data.results.length); //causes re-render of useEffect
        }
      },
    });
  };

  /*
   *  Scrolls to SyncOverview tab once linked account is added
   */

  const syncOverview = document.getElementById("sync-overview");

  const ScrollToSyncOverview = () => {
    syncOverview?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <TileStep
      img={img}
      title={title}
      subtitle={
        <>
          {subtitle}
          <div className="flex flex-col gap-y-3 mt-3">
            {linkToken && !orgHasReachedMaxLinkedAccounts && (
              <CustomAddTestLinkedAccountButton
                linkToken={linkToken}
                onNewLinkedAccount={(wasSuccess: boolean) => {
                  if (wasSuccess) {
                    fetchNewLinkedAccounts();
                    ScrollToSyncOverview();
                  }
                  fetchCreateTestLinkTokenConditionally();
                }}
              />
            )}
            {linkToken && !orgHasReachedMaxLinkedAccounts ? (
              <Text variant="sm">
                This button will create a free Test Linked Account. You can set up production Linked
                Accounts later.
              </Text>
            ) : (
              <Text variant="sm">You have reached the maximum number of Test Linked Accounts.</Text>
            )}
          </div>
        </>
      }
      stepNumber={stepNumber}
    />
  );
};

export default CustomTileStep;
